import type { TFunction } from 'i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { useCallback, useMemo, useState } from 'react';

import {
  useNamespace,
  usePreloadNamespace,
  useTranslation,
} from '@splitfiesta/i18n';
import { Alert, Button } from '@splitfiesta/ui';

import { withAuth } from '@/components/auth';
import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { TabContentLoader } from '@/components/common/TabContentLoader';
import { GroupTabs } from '@/components/groups/GroupTabs';
import { InvitationLink } from '@/components/groups/InvitationLink';
// Import section components
import { ExpensesSection } from '@/components/groups/sections/ExpensesSection';
import { GroupHeader } from '@/components/groups/sections/GroupHeader';
// Import widget components
import { BudgetWidget } from '@/components/groups/widgets/BudgetWidget';
import { TotalExpensesCard } from '@/components/groups/widgets/TotalExpensesCard';
import { UserBalanceCard } from '@/components/groups/widgets/UserBalanceCard';
import { AppLayout } from '@/components/layout/AppLayout';
import { GroupDetailSkeleton } from '@/components/skeleton';

import {
  useDeleteExpenseMutation,
  useGroupExpensesInfiniteQuery,
} from '@/hooks/queries/useExpenses';
import { useGroupWithDashboardQuery } from '@/hooks/queries/useGroups';
import { useEffectiveUserId } from '@/hooks/useEffectiveUserId';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useRouteTransition } from '@/hooks/useRouteTransition';

import { useAuthStore } from '@/stores/authStore';

import { ITEMS_PER_PAGE } from '@/constants/pagination';
import { handleApiError } from '@/utils/errorUtils';

function GroupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const user = useAuthStore(state => state.user);
  const { t, ready } = useTranslation();
  const { isTransitioning, startTransition } = useRouteTransition();
  const effectiveUserId = useEffectiveUserId();
  const navigate = useNavigate();

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Load required namespaces - must be at top level
  useNamespace('groups');
  useNamespace('expenses');
  useNamespace('settlements');
  useNamespace('currencies');
  useNamespace('common');
  useNamespace('members');
  useNamespace('dashboard');

  const allNamespacesLoaded = true;

  // Preload namespaces that will be needed for expense creation
  usePreloadNamespace('currencies');

  // Use React Query for group data
  const {
    data: group,
    isLoading: groupLoading,
    error: groupError,
  } = useGroupWithDashboardQuery(
    id ?? '',
    allNamespacesLoaded && !!user && !!id
  );

  // Use React Query for expenses with infinite scroll
  const {
    data: expensesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: expensesLoading,
  } = useGroupExpensesInfiniteQuery(
    id ?? '',
    {
      limit: ITEMS_PER_PAGE,
      ...(debouncedSearchQuery && { search: debouncedSearchQuery }),
    },
    allNamespacesLoaded && !!user && !!id
  );

  // Process expenses data
  const expenses = useMemo(() => {
    if (!expensesData?.pages) return [];
    return expensesData.pages.flatMap(page => page.expenses || []);
  }, [expensesData]);

  // Process dashboard data
  const dashboardData = useMemo(() => {
    if (!group?.dashboard) {
      return {
        totalSpent: 0,
        avgPerMember: 0,
        currency: group?.currency || 'USD',
        userBalance: group?.userBalance || 0,
      };
    }
    return {
      ...group.dashboard,
      userBalance: group.userBalance || 0,
    };
  }, [group]);

  const error = groupError
    ? handleApiError(groupError as Error, t as TFunction, 'groups')
    : null;
  const initialLoading = groupLoading;

  // Load more expenses for pagination
  const loadMoreExpenses = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Infinite scroll hook
  const lastExpenseElementRef = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasMore: hasNextPage,
    onLoadMore: loadMoreExpenses,
  });

  // Handle search input change
  const handleSearchChange = useCallback((value: string) => {
    setDebouncedSearchQuery(value);
  }, []);

  // Use delete mutation from React Query
  const deleteExpenseMutation = useDeleteExpenseMutation();

  // Early return after all hooks have been called
  if (!id) {
    return <Navigate to="/groups" replace />;
  }

  const handleDeleteExpense = async (expenseId: string) => {
    try {
      await deleteExpenseMutation.mutateAsync({
        expenseId,
        groupId: id,
      });
    } catch {
      // Reported by useDeleteExpenseMutation's onError toast; the
      // optimistic removal is rolled back there too.
    }
  };

  // Show skeleton immediately while translations or data are loading
  if (!ready || initialLoading) {
    return (
      <AppLayout>
        <GroupDetailSkeleton />
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <Alert type="error" message={error} className="mb-4" />
          <RouteChangeLink href="/groups">
            <Button variant="primary">
              {t('common:actions.back')} to {t('groups:list.title')}
            </Button>
          </RouteChangeLink>
        </div>
      </AppLayout>
    );
  }

  if (!group) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('groups:errors.groupNotFound')}
          </h3>
          <RouteChangeLink href="/groups">
            <Button variant="primary">
              {t('common:actions.back')} to {t('groups:list.title')}
            </Button>
          </RouteChangeLink>
        </div>
      </AppLayout>
    );
  }

  const handleTabClick = (href: string) => {
    startTransition(href);
  };

  return (
    <AppLayout>
      <div
        className="max-w-2xl mx-auto space-y-8"
        data-testid="group-dashboard"
      >
        {/* Header with Action Button */}
        <GroupHeader group={group} />

        {/* Tab Navigation */}
        <GroupTabs
          groupId={id}
          className="-mx-4 sm:-mx-6 lg:-mx-8 px-0 sm:px-6 lg:px-8"
          onTabClick={handleTabClick}
          pendingSettlementsCount={group.dashboard?.pendingSettlementsToConfirm}
        />

        {/* Show loading spinner when transitioning to another tab */}
        {isTransitioning ? (
          <TabContentLoader />
        ) : (
          <>
            {/* Widget Grid - Consistent spacing for all widgets */}
            <div className="grid grid-cols-1 gap-3 sm:gap-4 mb-3 sm:mb-4">
              {/* Row 1: Invitation Link - Full width (First on mobile) */}
              <InvitationLink
                groupId={id}
                groupName={group.name}
                userRole={group.userRole}
                onAddPlaceholder={() =>
                  navigate(`/groups/${id}/members`, {
                    state: { addPlaceholder: true },
                  })
                }
              />

              {/* Row 2: Balance and Total Expenses */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <UserBalanceCard
                  group={group}
                  userBalance={dashboardData.userBalance}
                />
                <TotalExpensesCard
                  group={group}
                  totalSpent={dashboardData.totalSpent}
                />
              </div>

              {/* Row 3: Budget Widget - Full width */}
              <BudgetWidget group={group} />
            </div>

            {/* Recent Expenses */}
            <ExpensesSection
              groupId={id}
              expenses={expenses}
              loading={expensesLoading}
              hasMore={hasNextPage}
              onLoadMore={loadMoreExpenses}
              onDelete={handleDeleteExpense}
              lastElementRef={lastExpenseElementRef}
              isFetchingNextPage={isFetchingNextPage}
              currentUserId={effectiveUserId}
              groupMembers={group?.balances?.map(b => ({
                userId: b.userId,
                role: b.role ?? 'member',
              }))}
              onSearchChange={handleSearchChange}
            />
          </>
        )}
      </div>
    </AppLayout>
  );
}

export default withAuth(GroupDetailPage);
